import { useState, useMemo } from "react";
import {
    Dialog,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    IconButton,
    Chip,
    Tooltip,
} from "@mui/material";
import { DataGrid, GridColDef, useGridApiRef } from "@mui/x-data-grid";
import {
    UploadFileOutlined,
    CloseOutlined,
    SearchOutlined,
    SeventeenMpOutlined,
    DirectionsCarOutlined,
    WifiOutlined,
    LocationOnOutlined,
} from "@mui/icons-material";
import { GRID_DEFAULT_LOCALE_TEXT } from "../../../../resources/location/utils/location";
import dayjs from "dayjs";
import { getTimezone } from "../../../../functions/functions";

// Função para gerar o Badge colorido de tempo de comunicação (Padrão do sistema)
const getLastPositionBadge = (date?: string) => {
    if (!date) {
        return { text: "sem dados", color: "#9e9e9e" };
    }

    const now = dayjs();
    const time = dayjs(date);

    const diffMinutes = now.diff(time, "minute");
    const diffHours = now.diff(time, "hour");
    const diffDays = now.diff(time, "day");

    if (diffHours < 1) {
        return { text: `há ${diffMinutes} minutos`, color: "#2e7d32" }; // verde
    }
    if (diffHours < 24) {
        return { text: `há ${diffHours} horas`, color: "#2e7d32" }; // verde
    }
    if (diffHours < 48) {
        return { text: `há ${diffHours} horas`, color: "#ed6c02" }; // laranja
    }
    return { text: `há ${diffDays} dias`, color: "#d32f2f" }; // vermelho
};

// Colunas da Grid padronizadas com Ícones de Cabeçalho e Estilos de Células
const defaultOfflineColumns: GridColDef[] =
    [{
        field: "name",
        headerName: "Nome/Etiqueta",
        align: "center",
        headerAlign: "center",
        width: 170,
        renderHeader: () => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#fff" }}>
                <SeventeenMpOutlined style={{ height: 20, width: 20, fill: "#fff" }} />
                <Typography variant="body2" sx={{ fontWeight: "bold", fontFamily: "Prometo", fontSize: "12px" }}>
                    Nome/Etiqueta
                </Typography>
            </Box>
        ),
        renderCell: (params) => (
            <Chip
                sx={{ background: "transparent", fontFamily: "Prometo" }}
                label={params.row?.name ?? "-"}
                icon={<SeventeenMpOutlined style={{ height: 22, width: 22, fill: "black" }} />}
            />
        ),
    },
    {
        field: "vehiclePlate",
        headerName: "Placa",
        align: "center",
        headerAlign: "center",
        width: 130,
        renderHeader: () => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#fff" }}>
                <DirectionsCarOutlined style={{ height: 20, width: 20, fill: "#fff" }} />
                <Typography variant="body2" sx={{ fontWeight: "bold", fontFamily: "Prometo", fontSize: "12px" }}>
                    Placa
                </Typography>
            </Box>
        ),
        renderCell: (params) => (
            <Chip
                sx={{ background: "transparent", fontFamily: "Prometo" }}
                label={params.row?.vehiclePlate ?? "-"}
                icon={<SeventeenMpOutlined style={{ height: 22, width: 22, fill: "black" }} />}
            />
        ),
    },
    {
        field: "deviceImei",
        headerName: "IMEI",
        align: "center",
        headerAlign: "center",
        width: 210,
        valueGetter: (_, row) => [row?.deviceImei, row?.satelliteImei].filter(Boolean).join(" / "),
        renderHeader: () => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#fff" }}>
                <SeventeenMpOutlined style={{ height: 20, width: 20, fill: "#fff" }} />
                <Typography variant="body2" sx={{ fontWeight: "bold", fontFamily: "Prometo", fontSize: "12px" }}>
                    IMEI
                </Typography>
            </Box>
        ),
        renderCell: (params) => {
            const imeiText = [params.row?.deviceImei, params.row?.satelliteImei].filter(Boolean).join(" / ") || "-";
            return (
                <Chip
                    sx={{ background: "transparent", fontFamily: "Prometo" }}
                    label={imeiText}
                    icon={<SeventeenMpOutlined style={{ height: 22, width: 22, fill: "black" }} />}
                />
            );
        },
    },
    {
        field: "fixTime",
        sortable: true,
        headerName: "Data/hora",
        headerAlign: "center",
        align: "center",
        width: 150,
        disableColumnMenu: true,
        valueGetter: (_, row) => {
            const fixTime = row?.position?.zonedFixTime;
            return fixTime ? dayjs(fixTime).valueOf() : 0;
        },
        renderHeader: () => (
            <WifiOutlined style={{ height: 24, width: 24, fill: "#fff" }} />
        ),
        renderCell: (item) => {
            const fixTime = item.row?.position?.zonedFixTime;
            const { text, color } = getLastPositionBadge(fixTime);
            return (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    {/* Data / Hora */}
                    <Typography className="map-grid-position-time" variant="body2" color="text.primary" fontSize={12}>
                        {fixTime
                            ? `${dayjs(fixTime).tz(getTimezone(item.row?.utcZone ?? "")).format("DD/MM/YYYY")} ${dayjs(fixTime).tz(getTimezone(item.row?.utcZone ?? "")).format("HH:mm:ss")}`
                            : "-"}
                    </Typography>

                    {/* Tarja */}
                    <Box
                        sx={{
                            backgroundColor: color,
                            color: "#fff",
                            width: "100%",
                            px: 1,
                            py: 0.25,
                            borderRadius: 0.5,
                            fontSize: 10,
                            fontWeight: 500,
                            lineHeight: 1.2,
                        }}
                    >
                        {text}
                    </Box>
                </Box>
            );
        },
    },
    {
        field: "address",
        headerName: "Última localização",
        headerClassName: "last-column",
        width: 260,
        valueGetter: (_, row) => {
            const address = row?.position?.address ?? "-";
            return address;
        },
        renderHeader: () => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#fff" }}>
                <LocationOnOutlined style={{ height: 20, width: 20, fill: "#fff" }} />
                <Typography variant="body2" sx={{ fontWeight: "bold", fontFamily: "Prometo", fontSize: "12px" }}>
                    Última localização
                </Typography>
            </Box>
        ),
        renderCell: (params) => {
            const address = params.row?.position?.address ?? "-";
            return (
                <Tooltip title={address}>
                    <Chip
                        sx={{ background: "transparent", fontFamily: "Prometo" }}
                        label={address}
                        icon={<LocationOnOutlined style={{ height: 22, width: 22, fill: "black" }} />}
                    />
                </Tooltip>
            );
        },
    }];

interface OfflineVehiclesDialogProps {
    open: boolean;
    onClose: () => void;
    offlineVehicles: any[];
    offlineColumns?: GridColDef[] | undefined;
}

const OfflineVehiclesDialog = ({
    open,
    onClose,
    offlineVehicles
}: OfflineVehiclesDialogProps) => {
    const [search, setSearch] = useState<string>("");
    const offlineApiRef = useGridApiRef();

    // Filtragem dinâmica por texto
    const filteredVehicles = useMemo(() => {
        if (!search) return offlineVehicles;
        const q = search.toLowerCase();
        return offlineVehicles.filter((row: any) => {
            const name = row?.name?.toLowerCase() || "";
            const plate = row?.vehiclePlate?.toLowerCase() || "";
            const imei = [row?.deviceImei, row?.satelliteImei].filter(Boolean).join(" ").toLowerCase();
            const address = row?.position?.address?.toLowerCase() || "";
            return name.includes(q) || plate.includes(q) || imei.includes(q) || address.includes(q);
        });
    }, [offlineVehicles, search]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            aria-labelledby="offline-vehicles-dialog-title"
            PaperProps={{
                className: "!tw-overflow-hidden !tw-rounded-2xl tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth",
            }}
            sx={{
                "& .MuiDialog-paper": {
                    width: { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)" },
                    maxHeight: "calc(100dvh - 24px)",
                    margin: { xs: 1.5, sm: 4 },
                    overflow: "hidden",
                    borderRadius: 4,
                    border: "1px solid rgba(0, 63, 74, 0.10)",
                    boxShadow: "0 24px 70px rgba(0, 0, 0, 0.28)",
                },
            }}
        >
            <Box className="offline-vehicles-dialog" sx={{ display: "flex", flexDirection: "column", minHeight: 0, backgroundColor: "#ffff" }}>
                <Box
                    className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-bg-gtr-sidebar tw-p-4 tw-text-white"
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1.5,
                        padding: { xs: 2, sm: 2.5 },
                        color: "#ffff",
                        backgroundImage: "linear-gradient(to right, #006C77, #09414A)",
                    }}
                >
                    <Typography
                        id="offline-vehicles-dialog-title"
                        variant="subtitle1"
                        sx={{
                            flex: 1,
                            minWidth: { xs: "100%", sm: 280 },
                            fontWeight: "bold",
                            fontFamily: "Prometo",
                            color: "#ffff",
                        }}
                    >
                        Veículos sem comunicação há mais de 24 horas ({filteredVehicles.length})
                    </Typography>

                    <TextField
                        size="small"
                        placeholder="Pesquisar..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{
                            width: { xs: "100%", sm: 280 },
                            '& .MuiInputBase-root': {
                                height: '42px',
                                fontFamily: "Prometo",
                                fontSize: '13px',
                                backgroundColor: "#fff",
                                borderRadius: 2,
                            },
                            "& fieldset": { border: "none" },
                        }}
                        InputProps={{
                            startAdornment: <SearchOutlined sx={{ fontSize: 18, mr: 0.5, color: "#666" }} />,
                            endAdornment: search && (
                                <Tooltip title="Limpar pesquisa" arrow placement="top">
                                    <IconButton size="small" aria-label="Limpar pesquisa" onClick={() => setSearch("")}>
                                        <CloseOutlined sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            ),
                        }}
                    />
                </Box>

                <Box className="offline-vehicles-content" sx={{ padding: { xs: 1.5, sm: 2.5 }, minHeight: 0, backgroundColor: "#ffff" }}>
                <Box className="offline-vehicles-grid" sx={{ height: { xs: "55dvh", sm: 420 }, width: "100%", overflow: "hidden", backgroundColor: "#fff", borderRadius: 3, border: "1px solid rgba(0, 63, 74, 0.12)" }}>
                    <DataGrid
                        apiRef={offlineApiRef}
                        rows={filteredVehicles}
                        columns={defaultOfflineColumns}
                        getRowId={(row) => row.id ?? row.deviceImei}
                        localeText={GRID_DEFAULT_LOCALE_TEXT}
                        density="compact"
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                    />
                </Box>

                <DialogActions sx={{ px: 0, pb: 0, pt: 2, gap: 1, flexWrap: "wrap" }}>
                    <Button
                        variant="contained"
                        startIcon={<UploadFileOutlined />}
                        onClick={() =>
                            offlineApiRef.current.exportDataAsCsv({
                                fileName: "veiculos-sem-comunicacao",
                                delimiter: ";",
                                fields: [
                                    "name",
                                    "vehiclePlate",
                                    "deviceImei",
                                    "fixTime",
                                    "address",
                                ],
                            })
                        }
                        sx={{
                            minWidth: 120,
                            minHeight: 42,
                            borderRadius: 2,
                            bgcolor: "#00B700",
                            color: "#fff",
                            fontFamily: "Prometo",
                            textTransform: "none",
                            boxShadow: "none",
                            transition: "transform 200ms ease, box-shadow 200ms ease",
                            '&:hover': { bgcolor: "#00B700", boxShadow: "0 8px 18px rgba(0, 183, 0, 0.22)", transform: "translateY(-1px)" },
                        }}
                    >
                        Exportar
                    </Button>

                    <Button
                        onClick={onClose}
                        variant="contained"
                        startIcon={<CloseOutlined />}
                        sx={{
                            minWidth: 120,
                            minHeight: 42,
                            borderRadius: 2,
                            bgcolor: "#dc3545",
                            color: "#fff",
                            fontFamily: "Prometo",
                            textTransform: "none",
                            boxShadow: "none",
                            transition: "transform 200ms ease, box-shadow 200ms ease",
                            '&:hover': { bgcolor: "#dc3545", boxShadow: "0 8px 18px rgba(220, 53, 69, 0.22)", transform: "translateY(-1px)" },
                        }}
                    >
                        Fechar
                    </Button>
                </DialogActions>
                </Box>
            </Box>
        </Dialog>
    );
};

export default OfflineVehiclesDialog;
